import { useState, useEffect, useCallback } from 'react';
import { liteClient } from 'algoliasearch/lite';
import { validateAlgoliaConfig } from '../config/algolia';

// Lazy initialization of Algolia client
let client: ReturnType<typeof liteClient> | null = null;

function getAlgoliaClient() {
  if (client) return client;
  
  try {
    const config = validateAlgoliaConfig();
    client = liteClient(config.appId, config.searchKey);
    console.log('✓ Algolia client initialized successfully');
    return client;
  } catch (error) {
    console.error('Failed to initialize Algolia client:', error);
    throw error;
  }
}

// Response cache with TTL (5 minutes)
interface CacheEntry {
  data: any;
  timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const responseCache = new Map<string, CacheEntry>();

function getCacheKey(indexName: string, query: string, filters: string, page: number): string {
  return `${indexName}:${query}:${filters}:${page}`;
}

function getCachedResponse(cacheKey: string): any | null {
  const cached = responseCache.get(cacheKey);
  if (!cached) return null;
  
  const age = Date.now() - cached.timestamp;
  if (age > CACHE_TTL) {
    responseCache.delete(cacheKey);
    console.log(`⏰ Cache expired for ${cacheKey} (age: ${Math.round(age / 1000)}s)`);
    return null;
  }
  
  console.log(`💾 Cache hit for ${cacheKey} (age: ${Math.round(age / 1000)}s)`);
  return cached.data;
}

function setCachedResponse(cacheKey: string, data: any): void {
  responseCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  
  // Clean up old entries if cache gets too large
  if (responseCache.size > 100) {
    const entries = Array.from(responseCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    // Remove oldest 20 entries
    entries.slice(0, 20).forEach(([key]) => responseCache.delete(key));
  }
}

// Clear all cached responses
export function clearSearchCache(): void {
  const count = responseCache.size;
  responseCache.clear();
  console.log(`🗑️ Search cache cleared (removed ${count} entries)`);
}

// Clear cache for specific index
export function clearIndexCache(indexName: string): void {
  const keys = Array.from(responseCache.keys());
  const toRemove = keys.filter(key => key.startsWith(indexName + ':'));
  toRemove.forEach(key => responseCache.delete(key));
  console.log(`🗑️ Cache cleared for index: ${indexName} (removed ${toRemove.length} entries)`);
}

export interface JobSearchFilters {
  categories?: string[];
  tags?: string[];
  contractTypes?: string[];
  isRemote?: boolean | null;
  experienceLevel?: string | null;
  hasSalary?: boolean | null;
  // Only include jobs posted within this many days (null/undefined = no cutoff, show all)
  postedWithinDays?: number | null;
}

export interface CompanySearchFilters {
  industry?: string | null;
  sector?: string | null;
  verificationStatus?: string;
}

/**
 * Hook for searching jobs using Algolia
 */
export function useJobSearch() {
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalHits, setTotalHits] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFiltersState] = useState<JobSearchFilters>({
    categories: [],
    tags: [],
    contractTypes: [],
    isRemote: null,
    experienceLevel: null,
    hasSalary: null,
    postedWithinDays: 90 // Default: past 3 months
  });

  // Wrapped setQuery with logging
  const setQuery = (newQuery: string) => {
    console.log('📝 [Job Search] Query changed:', newQuery || '(cleared)');
    setQueryState(newQuery);
  };

  // Wrapped setFilters with logging
  const setFilters = (newFilters: JobSearchFilters) => {
    console.log('🎯 [Job Search] Filters updated:', newFilters);
    setFiltersState(newFilters);
  };

  const search = useCallback(async (pageNum: number = 0) => {
    setLoading(true);
    setError(null);
    
    console.log('🔍 [Job Search] Starting search... (Fuzzy search enabled)');
    console.log('  Query:', query || '(empty)');
    console.log('  Page:', pageNum);
    
    try {
      // Build filter string for Algolia
      // NOTE: isActive filter disabled - add 'isActive' as filterOnly attribute in Algolia dashboard to enable
      let filterString = '';
      
      // Category filters - handle both top-level (mainCategoryId) and subcategories (categoryPath)
      if (filters.categories && filters.categories.length > 0) {
        const categoryFilters = filters.categories.map(cat => {
          // If category has no slash, it's top-level - use mainCategoryId
          if (!cat.includes('/')) {
            return `mainCategoryId:${cat}`;
          }
          // If category has slash, it's a subcategory - use categoryPath
          return `categoryPath:"${cat}"`;
        });
        const categoryFilter = categoryFilters.join(' OR ');
        filterString += (filterString ? ' AND ' : '') + `(${categoryFilter})`;
        console.log('  📂 Categories:', filters.categories.length, 'selected');
      }
      
      // Tag filters - tags is an array field in Algolia
      if (filters.tags && filters.tags.length > 0) {
        const tagFilter = filters.tags
          .map(tag => `tags:"${tag}"`)
          .join(' OR ');
        filterString += (filterString ? ' AND ' : '') + `(${tagFilter})`;
        console.log('  🏷️  Tags:', filters.tags.length, 'selected');
      }
      
      // Contract type filters
      if (filters.contractTypes && filters.contractTypes.length > 0) {
        const typeFilter = filters.contractTypes
          .map(type => `contractType:"${type}"`)
          .join(' OR ');
        filterString += (filterString ? ' AND ' : '') + `(${typeFilter})`;
        console.log('  💼 Contract types:', filters.contractTypes.join(', '));
      }
      
      // Remote filter
      if (filters.isRemote !== null) {
        filterString += (filterString ? ' AND ' : '') + `isRemote:${filters.isRemote}`;
        console.log('  🏠 Remote:', filters.isRemote);
      }
      
      // Experience level filter
      if (filters.experienceLevel) {
        filterString += (filterString ? ' AND ' : '') + `experienceLevel:"${filters.experienceLevel}"`;
        console.log('  📊 Experience:', filters.experienceLevel);
      }
      
      // Salary filter
      if (filters.hasSalary !== null) {
        if (filters.hasSalary) {
          filterString += (filterString ? ' AND ' : '') + `NOT salary:null`;
        }
        console.log('  💰 Salary:', filters.hasSalary ? 'must have' : 'any');
      }

      // Posted-date filter — createdAt is indexed as epoch milliseconds
      if (filters.postedWithinDays) {
        const cutoffMs = Date.now() - filters.postedWithinDays * 24 * 60 * 60 * 1000;
        filterString += (filterString ? ' AND ' : '') + `createdAt > ${cutoffMs}`;
        console.log('  📅 Posted within days:', filters.postedWithinDays);
      }

      console.log('  Final Algolia filter string:', filterString || '(none)');

      // Check cache first
      const cacheKey = getCacheKey('jobs', query, filterString, pageNum);
      const cached = getCachedResponse(cacheKey);
      
      if (cached) {
        console.log('💾 [Job Search] Cache HIT - Using cached results');
        console.log('  Results:', cached.hits.length, 'jobs');
        console.log('  Total hits:', cached.nbHits);
        setResults(cached.hits);
        setTotalHits(cached.nbHits);
        setPage(pageNum);
        setLoading(false);
        return;
      }

      console.log('⚡ [Job Search] Cache MISS - Fetching from Algolia...');
      
      // Fetch from Algolia with actual query and filters
      const result = await getAlgoliaClient().searchForHits([
        {
          indexName: 'jobs',
          params: {
            query,
            filters: filterString,
            hitsPerPage: 20,
            page: pageNum,
            // Fuzzy search configuration (typo tolerance)
            typoTolerance: true,
            minWordSizefor1Typo: 3,  // Allow 1 typo for words with 3+ characters
            minWordSizefor2Typos: 7, // Allow 2 typos for words with 7+ characters
            attributesToRetrieve: [
              'objectID',
              'title',
              'company',
              'location',
              'salary',
              'contractType',
              'category',
              'categoryId',
              'mainCategory',
              'mainCategoryId',
              'tags',
              'description',
              'isRemote',
              'experienceLevel',
              'postedDate',
              'createdAt',
              'isActive'
            ]
          }
        }
      ]);
      
      const { hits, nbHits = 0 } = result.results[0];
      
      // Map objectID to id for compatibility with existing UI code
      const mappedHits = hits.map((hit: any) => ({
        ...hit,
        id: hit.objectID || hit.id
      }));
      
      console.log('✓ [Job Search] Results received:', mappedHits.length, 'jobs out of', nbHits, 'total');
      
      // Cache the response
      setCachedResponse(cacheKey, { hits: mappedHits, nbHits });
      console.log('  Cached for future requests');
      
      setResults(mappedHits);
      setTotalHits(nbHits);
      setPage(pageNum);
    } catch (err: any) {
      console.error('❌ [Job Search] Algolia search error:', err);
      setError(err.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, filters]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      search(0);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [search]);

  const nextPage = () => {
    console.log('➡️ [Job Search] Next page:', page + 1);
    search(page + 1);
  };
  
  const prevPage = () => {
    const newPage = Math.max(0, page - 1);
    console.log('⬅️ [Job Search] Previous page:', newPage);
    search(newPage);
  };

  const refresh = () => {
    console.log('🔄 [Job Search] Refreshing current page:', page);
    search(page);
  };

  return { 
    query, 
    setQuery, 
    results, 
    loading,
    error,
    totalHits,
    page,
    filters, 
    setFilters,
    nextPage,
    prevPage,
    refresh,
    clearCache: () => clearIndexCache('jobs')
  };
}

/**
 * Hook for searching companies using Algolia
 */
export function useCompanySearch() {
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalHits, setTotalHits] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFiltersState] = useState<CompanySearchFilters>({
    industry: null,
    sector: null,
    verificationStatus: 'approved'
  });

  // Wrapped setQuery with logging
  const setQuery = (newQuery: string) => {
    console.log('📝 [Company Search] Query changed:', newQuery || '(cleared)');
    setQueryState(newQuery);
  };

  // Wrapped setFilters with logging
  const setFilters = (newFilters: CompanySearchFilters) => {
    console.log('🎯 [Company Search] Filters updated:', newFilters);
    setFiltersState(newFilters);
  };

  const search = useCallback(async (pageNum: number = 0) => {
    setLoading(true);
    setError(null);
    
    console.log('🏢 [Company Search] Starting search...');
    console.log('  Query:', query || '(empty)');
    console.log('  Page:', pageNum);
    
    try {
      // Build filter string
      let filterString = `isActive:true`;
      console.log('  Base filter:', filterString);
      
      // Only show approved companies
      if (filters.verificationStatus) {
        filterString += ` AND verificationStatus:"${filters.verificationStatus}"`;
        console.log('  + Verification filter:', filters.verificationStatus);
      }
      
      // Industry filter
      if (filters.industry) {
        filterString += ` AND industry:"${filters.industry}"`;
        console.log('  + Industry filter:', filters.industry);
      }
      
      // Sector filter
      if (filters.sector) {
        filterString += ` AND sector:"${filters.sector}"`;
        console.log('  + Sector filter:', filters.sector);
      }
      
      console.log('  Final Algolia filter string:', filterString);

      // Check cache first
      const cacheKey = getCacheKey('companies', query, filterString, pageNum);
      const cached = getCachedResponse(cacheKey);
      
      if (cached) {
        console.log('✓ [Company Search] Cache HIT - Using cached results');
        console.log('  Results:', cached.hits.length, 'companies');
        console.log('  Total hits:', cached.nbHits);
        setResults(cached.hits);
        setTotalHits(cached.nbHits);
        setPage(pageNum);
        setLoading(false);
        return;
      }

      console.log('⚡ [Company Search] Cache MISS - Fetching from Algolia...');
      // Fetch from Algolia
      const result = await getAlgoliaClient().searchForHits([
        {
          indexName: 'companies',
          params: {
            query,
            filters: filterString,
            hitsPerPage: 20,
            page: pageNum,
            attributesToRetrieve: [
              'objectID',
              'name',
              'description',
              'industry',
              'sector',
              'location',
              'logo',
              'verificationStatus',
              'jobCount',
              'isActive',
              'createdAt'
            ]
          }
        }
      ]);
      
      const { hits, nbHits = 0 } = result.results[0];
      
      console.log('✓ [Company Search] Algolia response received');
      console.log('  Results:', hits.length, 'companies');
      console.log('  Total hits:', nbHits);
      console.log('  Processing time:', result.results[0].processingTimeMS + 'ms');
      
      // Cache the response
      setCachedResponse(cacheKey, { hits, nbHits });
      console.log('  Cached for future requests');
      
      setResults(hits);
      setTotalHits(nbHits);
      setPage(pageNum);
    } catch (err: any) {
      console.error('❌ [Company Search] Algolia search error:', err);
      setError(err.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, filters]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      search(0);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const nextPage = () => {
    console.log('➡️ [Company Search] Next page:', page + 1);
    search(page + 1);
  };
  
  const prevPage = () => {
    const newPage = Math.max(0, page - 1);
    console.log('⬅️ [Company Search] Previous page:', newPage);
    search(newPage);
  };

  const refresh = () => {
    console.log('🔄 [Company Search] Refreshing current page:', page);
    search(page);
  };

  return { 
    query, 
    setQuery, 
    results, 
    loading,
    error,
    totalHits,
    page,
    filters, 
    setFilters,
    nextPage,
    prevPage,
    refresh,
    clearCache: () => clearIndexCache('companies')
  };
}

/**
 * Hook for autocomplete suggestions
 */
export function useJobAutocomplete(query: string) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const result = await getAlgoliaClient().searchForHits([
          {
            indexName: 'jobs',
            params: {
              query,
              hitsPerPage: 5,
              attributesToRetrieve: ['title', 'company']
            }
          }
        ]);
        
        const { hits } = result.results[0];
        
        const titleSuggestions = hits.map((hit: any) => hit.title);
        setSuggestions(titleSuggestions);
      } catch (err) {
        console.error('Autocomplete error:', err);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(timeoutId);
  }, [query]);

  return { suggestions, loading };
}
